import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { AnyObject } from 'shared/@types';
import { monitoring } from 'shared/library/monitoring';

const i18nInstances = new Map<string, ReturnType<typeof createInstance>>();
const reportedMissingTranslationKeys = new Set<string>();
let currentI18n = createInstance();

const missingTranslationReportDelayMs = 250;

function isTranslationObject(value: unknown): value is AnyObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeTranslationResources(current: unknown, incoming: unknown): unknown {
  if (!isTranslationObject(current) || !isTranslationObject(incoming)) {
    return incoming;
  }

  const merged: AnyObject = { ...current };

  for (const [key, value] of Object.entries(incoming)) {
    merged[key] = mergeTranslationResources(current[key], value);
  }

  return merged;
}

function addTranslationResources(i18n: ReturnType<typeof createInstance>, translations: AnyObject) {
  for (const [languageCode, namespaces] of Object.entries(translations)) {
    if (!isTranslationObject(namespaces)) {
      continue;
    }

    for (const [namespace, resources] of Object.entries(namespaces)) {
      if (!isTranslationObject(resources)) {
        continue;
      }

      const currentResources = i18n.getResourceBundle(languageCode, namespace);

      i18n.addResourceBundle(
        languageCode,
        namespace,
        mergeTranslationResources(currentResources, resources),
        false,
        true,
      );
    }
  }
}

function isMissingReportForActiveLanguage(languageList: readonly string[]) {
  const activeLanguages = [currentI18n.resolvedLanguage, currentI18n.language].filter(Boolean);

  return (
    activeLanguages.length === 0 || languageList.some((code) => activeLanguages.includes(code))
  );
}

function getCurrentRoute() {
  return typeof window !== 'undefined' ? window.location?.pathname : undefined;
}

function reportMissingTranslation(
  languages: readonly string[] | string,
  namespace: string,
  key: string,
) {
  const languageList = Array.isArray(languages) ? languages : [languages];
  const reportKey = `${languageList.join(',')}:${namespace}:${key}`;
  const route = getCurrentRoute();

  if (reportedMissingTranslationKeys.has(reportKey)) {
    return;
  }

  reportedMissingTranslationKeys.add(reportKey);

  globalThis.setTimeout(() => {
    const currentRoute = getCurrentRoute();

    if (route && currentRoute && route !== currentRoute) {
      reportedMissingTranslationKeys.delete(reportKey);
      return;
    }

    if (!isMissingReportForActiveLanguage(languageList)) {
      reportedMissingTranslationKeys.delete(reportKey);
      return;
    }

    const isStillMissing = languageList.every(
      (languageCode) => !currentI18n.exists(key, { lng: languageCode, ns: namespace }),
    );

    if (!isStillMissing) {
      reportedMissingTranslationKeys.delete(reportKey);
      return;
    }

    const error = new Error(`Missing translation: ${namespace}:${key}`);
    error.name = 'TranslationMissingError';

    monitoring.Sentry.captureException(error, {
      level: 'error',
      contexts: {
        translation: {
          languages: languageList,
          namespace,
          key,
          ...(route ? { route } : {}),
        },
      },
    });
  }, missingTranslationReportDelayMs);
}

export function initI18n(locale: string, translations: AnyObject) {
  const hasResources = Object.keys(translations).length > 0;
  const cachedLocaleI18n =
    typeof window !== 'undefined' && !hasResources ? i18nInstances.get(locale) : undefined;

  if (cachedLocaleI18n) {
    currentI18n = cachedLocaleI18n;
    return cachedLocaleI18n;
  }

  const safeLocale = translations[locale]
    ? locale
    : translations.en
      ? 'en'
      : (Object.keys(translations)[0] ?? 'en');
  const shouldCacheInstance = typeof window !== 'undefined' && hasResources;
  const cachedI18n = shouldCacheInstance ? i18nInstances.get(safeLocale) : undefined;

  if (cachedI18n) {
    addTranslationResources(cachedI18n, translations);
    currentI18n = cachedI18n;
    return cachedI18n;
  }

  const i18n = createInstance();
  const namespaces = Array.from(
    new Set([
      ...Object.keys(translations.en ?? {}),
      ...Object.keys(translations[safeLocale] ?? {}),
    ]),
  );

  i18n.use(initReactI18next).init({
    resources: translations,
    ns: namespaces,
    lng: safeLocale,
    fallbackLng: translations.en ? 'en' : false,
    supportedLngs: Object.keys(translations),
    debug: false,
    showSupportNotice: false,
    initImmediate: false,
    interpolation: {
      escapeValue: false,
      format: (value: unknown, format?: string) => {
        if (typeof value !== 'string') return value;
        switch (format) {
          case 'lowercase':
            return value.toLowerCase();
          case 'capitalize':
            return value.charAt(0).toUpperCase() + value.slice(1);
          case 'uppercase':
            return value.toUpperCase();
          default:
            return value;
        }
      },
    },
    saveMissing: true,
    missingKeyHandler: reportMissingTranslation,
  } as any);

  if (shouldCacheInstance) {
    i18nInstances.set(safeLocale, i18n);
  }

  currentI18n = i18n;

  return i18n;
}

export { currentI18n as i18n };
